import React, { createContext, useContext, useEffect, useState } from 'react';

import { dashboardUrl } from '@/config/urls';
import { NotificationResourceType } from '@/graphql';
import { Capacitor } from '@capacitor/core';
import { useSnackbar } from 'notistack';
import OneSignal from 'onesignal-cordova-plugin';
import { isNil } from 'ramda';
import { useNavigate } from 'react-router';
import { useLocalStorage } from 'react-use';

import { useAuth } from '../AuthProvider';
import { useEnvironment } from '../EnvironmentProvider';

const isCapacitor = Capacitor.isNativePlatform();

interface OneSignalContext {
	isInitialized: boolean;
	userPlayerId: string;
	getPermissionState: () => Promise<boolean>;
	showPermissionPrompt: () => Promise<boolean>;
	handlePushNotification: (isInForeground, notification) => void;
}

const defaultValue: OneSignalContext = {
	isInitialized: false,
	userPlayerId: '',
	getPermissionState: () => Promise.reject(),
	showPermissionPrompt: () => Promise.reject(),
	handlePushNotification: () => null,
};

const OneSignalContext = createContext<OneSignalContext>(defaultValue);

export function useOneSignal(): OneSignalContext {
	const context = useContext(OneSignalContext);
	if (isNil(context)) {
		throw new Error(
			`usePermissions must be used within an OneSignalProvider`,
		);
	}

	return context;
}

export const OneSignalProvider: React.FC = ({ children }) => {
	const navigate = useNavigate();
	const [isInitialized, setInitialized] = useState<boolean>(false);
	const [isSessionActive, setSessionActive] = useState<boolean>(false);
	const { enqueueSnackbar } = useSnackbar();
	const { user, updateUser, isAuthenticated } = useAuth();
	const [playerID, setPlayerID] = useState<string>('');
	const [localStorageToken, setLocalStorageToken] = useLocalStorage<
		boolean | null
	>('oneSignal-isPermissionRequested-v2', null);
	const {
		integrations: { onesignal },
	} = useEnvironment();

	useEffect(() => {
		if (!isCapacitor || isNil(onesignal.appId)) {
			return;
		}

		const runInit = async () => {
			// Notification clicked (foreground and background)
			OneSignal.Notifications.addEventListener('click', (event) => {
				handlePushNotification('click', event);
			});

			// Notification received, but not clicked (foreground)
			OneSignal.Notifications.addEventListener(
				'foregroundWillDisplay',
				(event) => {
					handlePushNotification('preview', event);
				},
			);

			// Get subscription ID and sync with API
			const subscriptionId =
				await OneSignal.User.pushSubscription.getIdAsync();
			const hasPermission =
				await OneSignal.Notifications.getPermissionAsync();
			const optedIn =
				await OneSignal.User.pushSubscription.getOptedInAsync();

			if (subscriptionId) {
				setPlayerID(subscriptionId);

				if (hasPermission) {
					if (!optedIn) {
						OneSignal.User.pushSubscription.optIn();
					}
				} else {
					if (!localStorageToken) {
						setLocalStorageToken(true);
						await showPermissionPrompt();
					}
				}
			} else {
				// Subscribe to get subscription ID when OneSignal finishes init
				OneSignal.User.pushSubscription.addEventListener(
					'change',
					async (subscription) => {
						const newId =
							await OneSignal.User.pushSubscription.getIdAsync();
						if (newId) {
							setPlayerID(newId);
							OneSignal.User.pushSubscription.optIn();
							setLocalStorageToken(true);
							await showPermissionPrompt();
						}
					},
				);
			}
		};

		if (isAuthenticated && !isSessionActive) {
			setInitialized(true);
			setSessionActive(true);

			OneSignal.initialize(onesignal.appId);
			runInit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		setInitialized,
		isAuthenticated,
		isSessionActive,
		onesignal.appId,
		enqueueSnackbar,
	]);

	useEffect(() => {
		const setUser = async () => {
			try {
				await updateUser({
					input: {
						notificationId: playerID,
					},
				});
				// OneSignal.setExternalUserId(user?.id);
			} catch (err) {
				console.error(err);
				enqueueSnackbar(
					'Something went wrong while updating the user',
					{
						variant: 'error',
					},
				);
			}
		};

		if (!isCapacitor || isNil(onesignal.appId)) {
			return;
		}
		if (playerID && user.notificationId !== playerID) {
			setUser();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playerID, updateUser, enqueueSnackbar, user.id, onesignal.appId]);

	useEffect(() => {
		if (!isCapacitor || isNil(onesignal.appId)) {
			return;
		}

		if (isInitialized && !isAuthenticated) {
			// OneSignal.disablePush(true);
			setSessionActive(false);
		}
	}, [isAuthenticated, isInitialized, onesignal.appId]);

	const getPermissionState = (): Promise<boolean> => {
		return new Promise((resolve, reject) => {
			// OneSignal.getDeviceState((state) => {
			// 	if (state.userId) {
			// 		if (
			// 			state.hasNotificationPermission &&
			// 			!state.pushDisabled
			// 		) {
			// 			resolve(true);
			// 		} else {
			// 			resolve(false);
			// 		}
			// 	} else {
			// 		reject();
			// 	}
			// });
		});
	};

	/**
	 * Prompt user to enable push notification.
	 * If already disabled, it falls back to notification settings and allows user to turn on notifications manually.
	 * @returns boolean indicating whether user enabled permissions or not
	 */
	const showPermissionPrompt = (): Promise<boolean> => {
		return new Promise((resolve) => {
			OneSignal.Notifications.requestPermission(false).then(
				(accepted: boolean) => {
					resolve(accepted);
				},
			);
		});
	};

	const handlePushNotification = (
		action: 'preview' | 'click',
		pushData: any,
	) => {
		const notification =
			action === 'preview'
				? pushData.getNotification()
				: pushData.notification;

		try {
			const { url, state } = parseNotificationResource(
				notification.additionalData.resourceType,
				notification.additionalData.resourceId,
			);
			if (action === 'preview') {
				// Show/hide notification from UI depending if the user is on the same URL
				if (location.pathname === '/' + url) {
					pushData.complete(undefined);
				} else {
					pushData.complete(notification);
				}
			} else {
				navigate(url, state);
			}
		} catch (err) {
			console.log(err);
		}
	};

	const parseNotificationResource = (
		resourceType: string,
		resourceId: string,
	): { url: string; state: any } => {
		let url = '';
		let state = {};

		switch (resourceType) {
			case NotificationResourceType.Dm:
			case NotificationResourceType.GroupChat: {
				url = 'messages/' + resourceId;
				break;
			}
			case NotificationResourceType.VideoChat: {
				url = 'videos/' + resourceId;
				state = { shouldChatBeOpened: true };
				break;
			}
			default:
				url = dashboardUrl;
		}
		return { url, state };
	};

	return (
		<OneSignalContext.Provider
			value={{
				isInitialized,
				userPlayerId: playerID,
				getPermissionState,
				showPermissionPrompt,
				handlePushNotification,
			}}
		>
			{children}
		</OneSignalContext.Provider>
	);
};

export default OneSignalProvider;
